import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { branchAPI, Branch } from "../../services/branchAPI";
import { useRBAC } from "../../hooks/useRBAC";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import SortableTableCell from "../../components/layout/SortableTableCell";
import TableActionButton from "../../components/layout/TableActionButton";
import { useTableSort } from "../../hooks/useTableSort";
import { tokens } from "../../theme";

type StatusFilter = "all" | "active" | "inactive";
type BranchSortField = "name" | "address" | "phone" | "email" | "status";

const branchCode = (id: string) => `BRN${id.slice(-6).toUpperCase()}`;

const BranchesPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    branch: Branch | null;
  }>({ open: false, branch: null });
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canCreate, canUpdate, canDelete, canRead } = useRBAC();
  const { sortBy, sortOrder, handleSort, sortParams } = useTableSort<BranchSortField>("name");
  const onSort = (field: BranchSortField) => handleSort(field, () => setPage(0));

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: branchesData, isLoading, error } = useQuery({
    queryKey: ["branches", page, rowsPerPage, debouncedSearch, statusFilter, sortBy, sortOrder],
    queryFn: () =>
      branchAPI.getBranches({
        pageNo: page + 1,
        pageSize: rowsPerPage,
        ...sortParams,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(statusFilter === "active" ? { isActive: true } : {}),
        ...(statusFilter === "inactive" ? { isActive: false } : {}),
      }),
  });

  const deleteBranchMutation = useMutation({
    mutationFn: branchAPI.deleteBranch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["branches"] });
      setDeleteDialog({ open: false, branch: null });
      setAlert({ type: "success", message: "Branch deleted successfully" });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to delete branch",
      });
    },
  });

  const branches = branchesData?.data || [];
  const totalCount = branchesData?.total || 0;

  const statusChip = (active: boolean) => (
    <Chip
      label={active ? "Active" : "Inactive"}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "0.75rem",
        bgcolor: active ? tokens.colors.success.light : tokens.colors.error.light,
        color: active ? tokens.colors.success.main : tokens.colors.error.main,
        border: "none",
      }}
    />
  );

  const filterDropdowns = (
    <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 140 } }}>
      <InputLabel>Status</InputLabel>
      <Select
        value={statusFilter}
        label="Status"
        onChange={(e) => {
          setStatusFilter(e.target.value as StatusFilter);
          setPage(0);
        }}
      >
        <MenuItem value="all">All Status</MenuItem>
        <MenuItem value="active">Active</MenuItem>
        <MenuItem value="inactive">Inactive</MenuItem>
      </Select>
    </FormControl>
  );

  return (
    <Box>
      <PageHeader
        title="Branches"
        subtitle="Manage branch locations and contact details"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Branches" },
        ]}
        action={
          <>
            <Tooltip title="Export PDF">
              <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider" }}>
                <PdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export Excel">
              <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider" }}>
                <ExcelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canCreate("branches") && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/branches/new")}
              >
                Add Branch
              </Button>
            )}
          </>
        }
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : "Failed to load branches"}
        </Alert>
      )}

      <DataListCard
        searchPlaceholder="Search name, address, phone or email…"
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        filters={filterDropdowns}
        footer={
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ width: "100%" }}
          />
        }
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <Typography color="text.secondary">Loading branches…</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <SortableTableCell label="Branch" field="name" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Address" field="address" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Phone" field="phone" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Email" field="email" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Status" field="status" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {branches.map((branch) => (
                  <TableRow key={branch.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="primary.main">
                        {branchCode(branch.id)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box minWidth={0}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {branch.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {branch.createdAt
                            ? format(new Date(branch.createdAt), "dd MMM yyyy")
                            : "Branch"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 180 }}>
                        {branch.address || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {branch.phone || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
                        {branch.email || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>{statusChip(branch.isActive)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {canRead("branches") && (
                          <TableActionButton
                            title="View branch"
                            onClick={() => navigate(`/branches/${branch.id}`)}
                          >
                            <ViewIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                        {canUpdate("branches") && (
                          <TableActionButton
                            title="Edit branch"
                            variant="primary"
                            onClick={() => navigate(`/branches/${branch.id}/edit`)}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                        {canDelete("branches") && (
                          <TableActionButton
                            title="Delete branch"
                            variant="danger"
                            onClick={() => setDeleteDialog({ open: true, branch })}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {branches.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No branches found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataListCard>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, branch: null })}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete branch</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteDialog.branch?.name}</strong>? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, branch: null })} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() =>
              deleteDialog.branch && deleteBranchMutation.mutate(deleteDialog.branch.id)
            }
            color="error"
            variant="contained"
            disabled={deleteBranchMutation.isPending}
          >
            {deleteBranchMutation.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BranchesPage;
